import { useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { api } from '../lib/api';
import { useToastStore } from '../store/toastStore';
import { Gavel } from 'lucide-react';

interface ChairOverridePanelProps {
  proposalId: string;
  proposalStatus: string;
  onSubmitted: () => void;
}

type Decision = 'approved' | 'approved_with_conditions' | 'parked' | 'rejected';

const DECISION_OPTIONS: { value: Decision; label: string; variant: 'default' | 'destructive' | 'secondary' | 'outline' }[] = [
  { value: 'approved', label: 'Approve', variant: 'default' },
  { value: 'approved_with_conditions', label: 'Approve with Conditions', variant: 'outline' },
  { value: 'parked', label: 'Park (Revisit Later)', variant: 'secondary' },
  { value: 'rejected', label: 'Reject', variant: 'destructive' },
];

export function ChairOverridePanel({ proposalId, proposalStatus, onSubmitted }: ChairOverridePanelProps) {
  const [decision, setDecision] = useState<Decision | ''>('');
  const [rationale, setRationale] = useState('');
  const [conditions, setConditions] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const showToast = useToastStore((s) => s.show);

  // Show this panel for any pre-decision status
  const allowedStatuses = ['submitted', 'under_triage', 'in_review', 'awaiting_decision', 'meeting_scheduled'];
  if (!allowedStatuses.includes(proposalStatus)) return null;

  const isValid = () => {
    if (!decision) return false;
    if (!rationale.trim()) return false;
    if (decision === 'approved_with_conditions' && !conditions.trim()) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!isValid()) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/verdicts/chair-override', {
        proposal_id: proposalId,
        decision,
        rationale,
        conditions: decision === 'approved_with_conditions' ? conditions : undefined,
      });
      showToast('Chair decision posted', 'success');
      onSubmitted();
    } catch (err: any) {
      setError(err.message || 'Failed to post decision');
    }
    setSubmitting(false);
  };

  return (
    <Card className="border-2 border-amber-200 bg-amber-50/30">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Gavel className="h-4 w-4 text-amber-700" />
            Chair Decision
          </CardTitle>
          <Badge variant="warning">Chair Override</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          As chair, you can approve or reject this proposal independently of the committee review process.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Decision *</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {DECISION_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                type="button"
                variant={decision === opt.value ? opt.variant : 'outline'}
                size="sm"
                onClick={() => { setDecision(opt.value); setError(''); }}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>

        {decision && (
          <>
            <div>
              <Label>Rationale *</Label>
              <Textarea
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
                rows={3}
                placeholder="Explain the reasoning behind this decision..."
                className="mt-1"
              />
            </div>

            {decision === 'approved_with_conditions' && (
              <div>
                <Label>Conditions *</Label>
                <Textarea
                  value={conditions}
                  onChange={(e) => setConditions(e.target.value)}
                  rows={3}
                  placeholder="List the conditions that must be met..."
                  className="mt-1"
                />
              </div>
            )}
          </>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end">
          <Button
            onClick={handleSubmit}
            disabled={!decision || submitting}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {submitting ? 'Posting...' : 'Post Chair Decision'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
